/**
 * 引导浮层组件（屏 4）
 *
 * 对抗中首次遇到新概念时弹出半透明提示。
 * 覆盖在 Canvas 战场上方，自动消失。
 *
 * 触发时机：
 * - 首次进入掩体 → 提示掩体效果
 * - 血量首次低于 50% → 提示找掩体
 * - 首次扫描到对手 → 提示开火时机
 *
 * 设计约束：Ardot v2.0 战术美学 + 半透明覆盖 + 自动消退
 */

export interface FeedbackTip {
  /** 唯一 ID（用于去重） */
  id: string;
  /** 提示文字 */
  text: string;
  /** 持续时间（ms），默认 4000 */
  duration?: number;
}

/** 已显示过的提示 ID 集合（跨 tick 持久） */
const shownTips = new Set<string>();

/**
 * 显示引导浮层（如果该提示尚未显示过）
 */
export function showFeedbackTip(container: HTMLElement, tip: FeedbackTip): void {
  if (shownTips.has(tip.id)) return;
  shownTips.add(tip.id);

  const el = document.createElement("div");
  el.className = "battle-feedback-tip";
  el.innerHTML = `
    <span class="battle-feedback-icon">◈</span>
    <span class="battle-feedback-text">${escapeHtml(tip.text)}</span>
  `;

  container.appendChild(el);

  // 入场动画
  requestAnimationFrame(() => {
    el.classList.add("battle-feedback-tip--visible");
  });

  // 自动消失
  const duration = tip.duration ?? 4000;
  setTimeout(() => {
    el.classList.remove("battle-feedback-tip--visible");
    el.classList.add("battle-feedback-tip--hiding");
    setTimeout(() => el.remove(), 400);
  }, duration);
}

/** 清除所有已显示的提示记录（用于新一局） */
export function resetFeedbackTips(): void {
  shownTips.clear();
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}

/** 预定义的引导提示 */
export const FEEDBACK_TIPS: Record<string, FeedbackTip> = {
  first_cover: {
    id: "first_cover",
    text: "这是掩体——站在掩体中可以减少对手火力造成的损伤",
    duration: 5000,
  },
  hp_low: {
    id: "hp_low",
    text: "状态值已低于安全线——考虑寻找掩体或改变策略",
    duration: 4000,
  },
  first_scan: {
    id: "first_scan",
    text: "态势扫描完成——现在你可以看到对手的位置了",
    duration: 3500,
  },
  cooldown_ready: {
    id: "cooldown_ready",
    text: "火力系统已就绪——可以投送火力了",
    duration: 3000,
  },
  enemy_visible: {
    id: "enemy_visible",
    text: "对手已进入态势感知范围——决定是机动还是火力投送",
    duration: 4000,
  },
  near_enemy: {
    id: "near_enemy",
    text: "你离对手很近——火力投送的命中率很高",
    duration: 3500,
  },
};
